import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { MessageDialog, ConfirmationDialog } from '../../shared/ui'
import { useApiError } from '../../shared/hooks'
import toast from 'react-hot-toast'
import apiClient from '../../shared/auth/services'
import Layout from '../components/Layout'

// Validation schema for logged-in users
// Helper function to validate Katakana
const katakanaRegex = /^[ァ-ヶー\s]*$/
const validateKatakana = (value) => {
    if (!value || value.trim() === '') return true // Empty is handled by nonempty
    return katakanaRegex.test(value)
}

const companyInfoSchema = z.object({
    companyLoginId: z.string().optional(),
    companyName: z.string().nonempty('会社名を入力してください'),
    companyKana: z.string()
        .nonempty('会社名（カナ）を入力してください')
        .refine(validateKatakana, '会社名（カナ）はカタカナで入力してください'),
    url: z.string().optional(),
    zipCode1: z.string().nonempty('郵便番号を入力してください'),
    zipCode2: z.string().nonempty('郵便番号を入力してください'),
    todofuken: z.string().optional(),
    shikuchoson: z.string().nonempty('市区町村を入力してください'),
    address: z.string().nonempty('住所を入力してください'),
    tel1: z.string().nonempty('電話番号を入力してください'),
    tel2: z.string().nonempty('電話番号を入力してください'),
    tel3: z.string().nonempty('電話番号を入力してください'),
    fax1: z.string().optional(),
    fax2: z.string().optional(),
    fax3: z.string().optional(),
    tantoBushomei: z.string().optional(),
    tantoSei: z.string().nonempty('担当者姓を入力してください'),
    tantoMei: z.string().nonempty('担当者名を入力してください'),
    tantoSeiKana: z.string()
        .nonempty('担当者姓（カナ）を入力してください')
        .refine(validateKatakana, '担当者姓（カナ）はカタカナで入力してください'),
    tantoMeiKana: z.string()
        .nonempty('担当者名（カナ）を入力してください')
        .refine(validateKatakana, '担当者名（カナ）はカタカナで入力してください'),
    tantoMail: z.email('有効なメールアドレスを入力してください'),
    mobile1: z.string().optional(),
    mobile2: z.string().optional(),
    mobile3: z.string().optional(),
    seikyuTantoSei: z.string().nonempty('請求書送付先担当者姓を入力してください'),
    seikyuTantoMei: z.string().nonempty('請求書送付先担当者名を入力してください'),
    seikyuTantoSeiKana: z.string()
        .nonempty('請求書送付先担当者姓（カナ）を入力してください')
        .refine(validateKatakana, '請求書送付先担当者姓（カナ）はカタカナで入力してください'),
    seikyuTantoMeiKana: z.string()
        .nonempty('請求書送付先担当者名（カナ）を入力してください')
        .refine(validateKatakana, '請求書送付先担当者名（カナ）はカタカナで入力してください'),
    seikyuTantoMail: z.email('有効なメールアドレスを入力してください'),
    changeRiyoPlan: z.string().optional(),
    pixClientUsage: z.union([z.literal(0), z.literal(1)]).optional(),
    pixCameraUsage: z.union([z.literal(0), z.literal(1)]).optional(),
    voiceRecognitionUsage: z.union([z.literal(0), z.literal(1)]).optional(),
    ocrUsage: z.union([z.literal(0), z.literal(1)]).optional(),
    thetaCameraUsage: z.union([z.literal(0), z.literal(1)]).optional()
})

const KigyoJohoKanri = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [isSearchingAddress, setIsSearchingAddress] = useState(false)
    const [prefectures, setPrefectures] = useState([])
    const [currentPlan, setCurrentPlan] = useState('')
    const [currentPlanValue, setCurrentPlanValue] = useState('')
    const [isPlanChangeLoading, setIsPlanChangeLoading] = useState(false)
    const [isCancellationLoading, setIsCancellationLoading] = useState(false)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [confirmAction, setConfirmAction] = useState(null)
    const [confirmMessage, setConfirmMessage] = useState('')

    const {
        showErrorDialog,
        errorMessage,
        handleApiError,
        handleApiResponse,
        handleValidationErrors,
        closeErrorDialog
    } = useApiError()

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        trigger,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(companyInfoSchema),
        mode: 'onBlur'
    })

    const watchedZipCode1 = watch('zipCode1')
    const watchedZipCode2 = watch('zipCode2')
    const watchedChangeRiyoPlan = watch('changeRiyoPlan')

    const setCompanyData = (data) => {
        setValue('companyLoginId', data.companyLoginId || '')
        setValue('companyName', data.companyName || '')
        setValue('companyKana', data.companyKana || '')
        setValue('url', data.url || '')
        setValue('zipCode1', data.zipCode1 || '')
        setValue('zipCode2', data.zipCode2 || '')
        setValue('todofuken', data.todofuken || '')
        setValue('shikuchoson', data.shikuchoson || '')
        setValue('address', data.address || '')
        setValue('tel1', data.tel1 || '')
        setValue('tel2', data.tel2 || '')
        setValue('tel3', data.tel3 || '')
        setValue('fax1', data.fax1 || '')
        setValue('fax2', data.fax2 || '')
        setValue('fax3', data.fax3 || '')
        setValue('tantoBushomei', data.tantoBushomei || '')
        setValue('tantoSei', data.tantoSei || '')
        setValue('tantoMei', data.tantoMei || '')
        setValue('tantoSeiKana', data.tantoSeiKana || '')
        setValue('tantoMeiKana', data.tantoMeiKana || '')
        setValue('tantoMail', data.tantoMail || '')
        setValue('mobile1', data.mobile1 || '')
        setValue('mobile2', data.mobile2 || '')
        setValue('mobile3', data.mobile3 || '')
        setValue('seikyuTantoSei', data.seikyuTantoSei || '')
        setValue('seikyuTantoMei', data.seikyuTantoMei || '')
        setValue('seikyuTantoSeiKana', data.seikyuTantoSeiKana || '')
        setValue('seikyuTantoMeiKana', data.seikyuTantoMeiKana || '')
        setValue('seikyuTantoMail', data.seikyuTantoMail || '')
        setValue('pixClientUsage', data.pixClientUsage ?? 0)
        setValue('pixCameraUsage', data.pixCameraUsage ?? 0)
        setValue('voiceRecognitionUsage', data.voiceRecognitionUsage ?? 0)
        setValue('ocrUsage', data.ocrUsage ?? 0)
        setValue('thetaCameraUsage', data.thetaCameraUsage ?? 0)

        setCurrentPlanValue(data.riyoPlan || '')
        setCurrentPlan(getPlanName(data.riyoPlan))
    }

    useEffect(() => {
        const loadInitialData = async () => {
            setIsLoading(true)
            try {
                const prefecturesResponse = await apiClient.post('/getTodofukenList', {})
                if (handleApiResponse(prefecturesResponse.data, '都道府県の取得に失敗しました。')) {
                    setIsLoading(false)
                    return
                }
                setPrefectures(prefecturesResponse.data.todofukenList || [])

                const companyResponse = await apiClient.post('/getCompany', {})
                if (handleApiResponse(companyResponse.data, '会社情報の取得に失敗しました。')) {
                    setIsLoading(false)
                    return
                }

                setCompanyData(companyResponse.data)
            } catch (error) {
                handleApiError(error, 'データの取得に失敗しました。')
            } finally {
                setIsLoading(false)
            }
        }

        loadInitialData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Empty dependency array - only run once on mount

    const getBorderClass = (fieldName, baseClass) => {
        return `${baseClass} ${errors[fieldName] ? 'border-red-500' : 'border-gray-300'}`
    }

    const hasGroupError = (fieldNames) => {
        return fieldNames.some(fieldName => errors[fieldName])
    }

    const getGroupErrorMessage = (fieldNames) => {
        for (const fieldName of fieldNames) {
            if (errors[fieldName]) {
                return errors[fieldName].message
            }
        }
        return null
    }

    const getGroupBorderClass = (fieldNames, baseClass) => {
        return `${baseClass} ${hasGroupError(fieldNames) ? 'border-red-500' : 'border-gray-300'}`
    }

    const onError = () => {
        handleValidationErrors('必須項目が入力されていません。入力を確認してください。')
    }



    const getPlanName = (planValue) => {
        switch (planValue) {
            case "0": return "デモプラン"
            case "1": return "スタートプラン"
            case "2": return "スタンダードプラン"
            case "3": return "プラチナプラン"
            case "4": return "エンタープライズプラン"
            default: return ""
        }
    }

    const searchAddressByZipCode = async () => {
        const zipCode1 = watchedZipCode1
        const zipCode2 = watchedZipCode2

        if (!zipCode1 || !zipCode2) {
            handleValidationErrors('郵便番号を入力してください。')
            return
        }

        setIsSearchingAddress(true)
        try {
            const response = await apiClient.post('/getAddress', {
                zipCode1: zipCode1,
                zipCode2: zipCode2
            })

            if (handleApiResponse(response.data, '住所の取得に失敗しました。')) {
                return
            }

            setValue('todofuken', response.data.todofuken)
            setValue('shikuchoson', response.data.shikuchoson)
            setValue('address', response.data.address)
            toast.success('住所を取得しました')
        } catch (error) {
            handleApiError(error, '住所の取得に失敗しました。')
        } finally {
            setIsSearchingAddress(false)
        }
    }

    const validateNumeric = (value) => {
        return value.replace(/[^0-9]/g, '')
    }

    const onSubmit = async (data) => {
        setIsLoading(true)

        try {
            const response = await apiClient.post('/updateCompany', {
                use_init: "true",
                companyLoginId: data.companyLoginId,
                companyName: data.companyName,
                companyKana: data.companyKana,
                url: data.url || '',
                zipCode1: data.zipCode1,
                zipCode2: data.zipCode2,
                todofuken: data.todofuken,
                shikuchoson: data.shikuchoson,
                address: data.address,
                tel1: data.tel1,
                tel2: data.tel2,
                tel3: data.tel3,
                fax1: data.fax1 || '',
                fax2: data.fax2 || '',
                fax3: data.fax3 || '',
                tantoBushomei: data.tantoBushomei || '',
                tantoSei: data.tantoSei,
                tantoMei: data.tantoMei,
                tantoSeiKana: data.tantoSeiKana,
                tantoMeiKana: data.tantoMeiKana,
                tantoMail: data.tantoMail,
                mobile1: data.mobile1 || '',
                mobile2: data.mobile2 || '',
                mobile3: data.mobile3 || '',
                seikyuTantoSei: data.seikyuTantoSei,
                seikyuTantoMei: data.seikyuTantoMei,
                seikyuTantoSeiKana: data.seikyuTantoSeiKana,
                seikyuTantoMeiKana: data.seikyuTantoMeiKana,
                seikyuTantoMail: data.seikyuTantoMail,
                riyoPlan: currentPlanValue,
                pixClientUsage: data.pixClientUsage ?? 0,
                pixCameraUsage: data.pixCameraUsage ?? 0,
                voiceRecognitionUsage: data.voiceRecognitionUsage ?? 0,
                ocrUsage: data.ocrUsage ?? 0,
                thetaCameraUsage: data.thetaCameraUsage ?? 0
            })

            if (handleApiResponse(response.data, '更新に失敗しました。')) {
                return
            }

            toast.success('登録が完了しました。')
            window.location.reload()
        } catch (error) {
            handleApiError(error, '更新に失敗しました。')
        } finally {
            setIsLoading(false)
        }
    }

    const handlePlanChange = async () => {
        if (!watchedChangeRiyoPlan) {
            handleValidationErrors('変更したいプランを選択してください。')
            return
        }

        setConfirmAction('planChange')
        setConfirmMessage('プラン変更申請を行います。よろしいですか？')
        setShowConfirmDialog(true)
    }

    const handleCancellation = async () => {
        setConfirmAction('cancellation')
        setConfirmMessage('解約手続きを行います。よろしいですか？')
        setShowConfirmDialog(true)
    }

    const handleConfirmAction = async () => {
        setShowConfirmDialog(false)

        if (confirmAction === 'planChange') {
            setIsPlanChangeLoading(true)
            try {
                const response = await apiClient.post('/sendKigyoRequest', {
                    requestKbn: 'planChange',
                    changeRiyoPlan: watchedChangeRiyoPlan
                })

                if (handleApiResponse(response.data, 'プラン変更申請に失敗しました。')) {
                    return
                }

                toast.success('受付しました。担当者からご連絡させていただきます。お返事までに2～3日いただく場合がございます。')
                setValue('changeRiyoPlan', '')
            } catch (error) {
                handleApiError(error, 'プラン変更申請に失敗しました。')
            } finally {
                setIsPlanChangeLoading(false)
            }
        } else if (confirmAction === 'cancellation') {
            setIsCancellationLoading(true)
            try {
                const response = await apiClient.post('/sendKigyoRequest', {
                    requestKbn: 'cancellation'
                })

                if (handleApiResponse(response.data, '解約手続きに失敗しました。')) {
                    return
                }

                toast.success('受付しました。担当者からご連絡させていただきます。お返事までに2～3日いただく場合がございます。')
            } catch (error) {
                handleApiError(error, '解約手続きに失敗しました。')
            } finally {
                setIsCancellationLoading(false)
            }
        }
    }

    const closeConfirmDialog = () => {
        setShowConfirmDialog(false)
        setConfirmAction(null)
        setConfirmMessage('')
    }



    return (
        <Layout pageTitle="企業情報管理">
            <div className="max-w-6xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
                    <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-8">

                        {/* 会社情報 Section */}
                        <div className="space-y-6">
                            <div className="bg-gray-800 text-white px-4 py-2 rounded">
                                会社情報
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label htmlFor="companyLoginId" className="font-medium text-gray-900">企業ID *</label>
                                <div className="md:col-span-2">
                                    <input
                                        id="companyLoginId"
                                        type="text"
                                        {...register('companyLoginId')}
                                        disabled
                                        className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label htmlFor="companyName" className="font-medium text-gray-900">会社名 *</label>
                                <div className="md:col-span-2">
                                    <input
                                        id="companyName"
                                        type="text"
                                        {...register('companyName')}
                                        onChange={(e) => {
                                            setValue('companyName', e.target.value)
                                            trigger('companyName')
                                        }}
                                        className={getBorderClass('companyName', 'w-full px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                        placeholder="(例)株式会社MIC"
                                    />
                                    {errors.companyName && (
                                        <div className="text-red-500 text-xs mt-1">
                                            {errors.companyName.message}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label htmlFor="companyKana" className="font-medium text-gray-900">会社名（カナ） *</label>
                                <div className="md:col-span-2">
                                    <input
                                        id="companyKana"
                                        type="text"
                                        {...register('companyKana')}
                                        onChange={(e) => {
                                            setValue('companyKana', e.target.value)
                                            trigger('companyKana')
                                        }}
                                        onBlur={() => {
                                            trigger('companyKana')
                                        }}
                                        className={getBorderClass('companyKana', 'w-full px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                        placeholder="(例)カブシキガイシャエムアイシー"
                                    />
                                    {errors.companyKana && (
                                        <div className="text-red-500 text-xs mt-1">
                                            {errors.companyKana.message}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label htmlFor="url" className="font-medium text-gray-900">ホームページURL</label>
                                <div className="md:col-span-2">
                                    <input
                                        id="url"
                                        type="text"
                                        {...register('url')}
                                        onChange={(e) => {
                                            setValue('url', e.target.value)
                                            trigger('url')
                                        }}
                                        className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                                        placeholder="(例)https://www.inter-mic.co.jp/"
                                    />
                                </div>
                            </div>

                            {/* Address Section */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label className="font-medium text-gray-900">住所 *</label>
                                <div className="md:col-span-2 space-y-2">
                                    <div className="flex gap-2 items-center">
                                        <div>
                                            <label htmlFor="zipCode1" className="sr-only">郵便番号 1</label>
                                            <input
                                                id="zipCode1"
                                                type="text"
                                                maxLength="3"
                                                {...register('zipCode1')}
                                                onChange={(e) => {
                                                    const value = validateNumeric(e.target.value)
                                                    setValue('zipCode1', value)
                                                    trigger('zipCode1')
                                                }}
                                                className={getGroupBorderClass(['zipCode1', 'zipCode2'], 'w-16 text-center text-gray-900 px-2 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            />
                                        </div>
                                        <span className='text-gray-500'>-</span>
                                        <div>
                                            <label htmlFor="zipCode2" className="sr-only">郵便番号 2</label>
                                            <input
                                                id="zipCode2"
                                                type="text"
                                                maxLength="4"
                                                {...register('zipCode2')}
                                                onChange={(e) => {
                                                    const value = validateNumeric(e.target.value)
                                                    setValue('zipCode2', value)
                                                    trigger('zipCode2')
                                                }}
                                                className={getGroupBorderClass(['zipCode1', 'zipCode2'], 'w-20 text-center px-2 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={searchAddressByZipCode}
                                            disabled={isSearchingAddress}
                                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
                                        >
                                            {isSearchingAddress ? '検索中...' : '郵便番号から検索'}
                                        </button>
                                    </div>
                                    {getGroupErrorMessage(['zipCode1', 'zipCode2']) && (
                                        <div className="text-red-500 text-xs mt-1">
                                            {getGroupErrorMessage(['zipCode1', 'zipCode2'])}
                                        </div>
                                    )}
                                    <div className="flex items-start gap-2">
                                        <div className="flex flex-col">
                                            <label htmlFor="todofuken" className="sr-only">都道府県</label>
                                            <select
                                                id="todofuken"
                                                {...register('todofuken')}
                                                onChange={(e) => {
                                                    setValue('todofuken', e.target.value)
                                                    trigger('todofuken')
                                                }}
                                                className={getBorderClass('todofuken', 'inline-block px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            >
                                                {prefectures.map((pref) => (
                                                    <option key={pref.todofukenCd} value={pref.todofukenCd}>
                                                        {pref.todofukenName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex flex-col w-full">
                                            <div className="flex-1">
                                                <label htmlFor="shikuchoson" className="sr-only">市区町村</label>
                                                <input
                                                    id="shikuchoson"
                                                    type="text"
                                                    {...register('shikuchoson')}
                                                    onChange={(e) => {
                                                        setValue('shikuchoson', e.target.value)
                                                        trigger('shikuchoson')
                                                    }}
                                                    className={getBorderClass('shikuchoson', 'w-full px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                                    placeholder="(例)千代田区"
                                                />
                                                {errors.shikuchoson && (
                                                    <div className="text-red-500 text-xs mt-1">
                                                        {errors.shikuchoson.message}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="address" className="sr-only">住所</label>
                                        <input
                                            id="address"
                                            type="text"
                                            {...register('address')}
                                            onChange={(e) => {
                                                setValue('address', e.target.value)
                                                trigger('address')
                                            }}
                                            className={getBorderClass('address', 'w-full px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            placeholder="(例)神田駿河台2-5-7 村田ビル6F"
                                        />
                                        {errors.address && (
                                            <div className="text-red-500 text-xs mt-1">
                                                {errors.address.message}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label className="font-medium text-gray-900">TEL *</label>
                                <div className="md:col-span-2">
                                    <div className="flex gap-2">
                                        <div>
                                            <label htmlFor="tel1" className="sr-only">TEL 1</label>
                                            <input
                                                id="tel1"
                                                type="text"
                                                maxLength="5"
                                                {...register('tel1')}
                                                onChange={(e) => {
                                                    const value = validateNumeric(e.target.value)
                                                    setValue('tel1', value)
                                                    trigger('tel1')
                                                }}
                                                className={getGroupBorderClass(['tel1', 'tel2', 'tel3'], 'w-24 text-center px-2 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="tel2" className="sr-only">TEL 2</label>
                                            <input
                                                id="tel2"
                                                type="text"
                                                maxLength="5"
                                                {...register('tel2')}
                                                onChange={(e) => {
                                                    const value = validateNumeric(e.target.value)
                                                    setValue('tel2', value)
                                                    trigger('tel2')
                                                }}
                                                className={getGroupBorderClass(['tel1', 'tel2', 'tel3'], 'w-24 text-center px-2 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="tel3" className="sr-only">TEL 3</label>
                                            <input
                                                id="tel3"
                                                type="text"
                                                maxLength="5"
                                                {...register('tel3')}
                                                onChange={(e) => {
                                                    const value = validateNumeric(e.target.value)
                                                    setValue('tel3', value)
                                                    trigger('tel3')
                                                }}
                                                className={getGroupBorderClass(['tel1', 'tel2', 'tel3'], 'w-24 text-center px-2 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            />
                                        </div>
                                    </div>
                                    {getGroupErrorMessage(['tel1', 'tel2', 'tel3']) && (
                                        <div className="md:col-span-2">
                                            <div className="text-red-500 text-xs mt-1">
                                                {getGroupErrorMessage(['tel1', 'tel2', 'tel3'])}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label className="font-medium text-gray-900">FAX</label>
                                <div className="md:col-span-2 flex gap-2">
                                    <div>
                                        <label htmlFor="fax1" className="sr-only">FAX 1</label>
                                        <input
                                            id="fax1"
                                            type="text"
                                            maxLength="5"
                                            {...register('fax1')}
                                            onChange={(e) => {
                                                const value = validateNumeric(e.target.value)
                                                setValue('fax1', value)
                                                trigger('fax1')
                                            }}
                                            className="w-24 text-center px-2 py-2 text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="fax2" className="sr-only">FAX 2</label>
                                        <input
                                            id="fax2"
                                            type="text"
                                            maxLength="5"
                                            {...register('fax2')}
                                            onChange={(e) => {
                                                const value = validateNumeric(e.target.value)
                                                setValue('fax2', value)
                                                trigger('fax2')
                                            }}
                                            className="w-24 text-center px-2 py-2 text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="fax3" className="sr-only">FAX 3</label>
                                        <input
                                            id="fax3"
                                            type="text"
                                            maxLength="5"
                                            {...register('fax3')}
                                            onChange={(e) => {
                                                const value = validateNumeric(e.target.value)
                                                setValue('fax3', value)
                                                trigger('fax3')
                                            }}
                                            className="w-24 text-center px-2 py-2 text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ご担当者様情報 Section */}
                        <div className="space-y-6">
                            <div className="bg-gray-800 text-white px-4 py-2 rounded">
                                ご担当者様情報
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label htmlFor="tantoBushomei" className="font-medium text-gray-900">部署名</label>
                                <div className="md:col-span-2">
                                    <input
                                        id="tantoBushomei"
                                        type="text"
                                        {...register('tantoBushomei')}
                                        onChange={(e) => {
                                            setValue('tantoBushomei', e.target.value)
                                            trigger('tantoBushomei')
                                        }}
                                        className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                                        placeholder="(例)営業部"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                                <label className="font-medium text-gray-900">担当者名 *</label>
                                <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
                                    <div className="flex-1">
                                        <label htmlFor="tantoSei" className="sr-only">担当者姓</label>
                                        <input
                                            id="tantoSei"
                                            type="text"
                                            {...register('tantoSei')}
                                            onChange={(e) => {
                                                setValue('tantoSei', e.target.value)
                                                trigger('tantoSei')
                                            }}
                                            className={getBorderClass('tantoSei', 'flex-1 w-full px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            placeholder="(例)姓"
                                        />
                                        {errors.tantoSei && (
                                            <div className="text-red-500 text-xs mt-1">
                                                {errors.tantoSei.message}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <label htmlFor="tantoMei" className="sr-only">担当者名</label>
                                        <input
                                            id="tantoMei"
                                            type="text"
                                            {...register('tantoMei')}
                                            onChange={(e) => {
                                                setValue('tantoMei', e.target.value)
                                                trigger('tantoMei')
                                            }}
                                            className={getBorderClass('tantoMei', 'flex-1 w-full px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            placeholder="(例)名"
                                        />
                                        {errors.tantoMei && (
                                            <div className="text-red-500 text-xs mt-1">
                                                {errors.tantoMei.message}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                                <label className="font-medium text-gray-900">担当者名（カナ） *</label>
                                <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
                                    <div className="flex-1">
                                        <label htmlFor="tantoSeiKana" className="sr-only">担当者姓（カナ）</label>
                                        <input
                                            id="tantoSeiKana"
                                            type="text"
                                            {...register('tantoSeiKana')}
                                            onChange={(e) => {
                                                setValue('tantoSeiKana', e.target.value)
                                                trigger('tantoSeiKana')
                                            }}
                                            onBlur={() => {
                                                trigger('tantoSeiKana')
                                            }}
                                            className={getBorderClass('tantoSeiKana', 'w-full flex-1 px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            placeholder="(例)セイ"
                                        />
                                        {errors.tantoSeiKana && (
                                            <div className="text-red-500 text-xs mt-1">
                                                {errors.tantoSeiKana.message}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <label htmlFor="tantoMeiKana" className="sr-only">担当者名（カナ）</label>
                                        <input
                                            id="tantoMeiKana"
                                            type="text"
                                            {...register('tantoMeiKana')}
                                            onChange={(e) => {
                                                setValue('tantoMeiKana', e.target.value)
                                                trigger('tantoMeiKana')
                                            }}
                                            onBlur={() => {
                                                trigger('tantoMeiKana')
                                            }}
                                            className={getBorderClass('tantoMeiKana', 'w-full flex-1 px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            placeholder="(例)メイ"
                                        />
                                        {errors.tantoMeiKana && (
                                            <div className="text-red-500 text-xs mt-1">
                                                {errors.tantoMeiKana.message}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label htmlFor="tantoMail" className="font-medium text-gray-900">メールアドレス *</label>
                                <div className="md:col-span-2">
                                    <input
                                        id="tantoMail"
                                        type="email"
                                        {...register('tantoMail')}
                                        onChange={(e) => {
                                            setValue('tantoMail', e.target.value)
                                            trigger('tantoMail')
                                        }}
                                        className={getBorderClass('tantoMail', 'w-full px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                        placeholder="(例)mail@inter-mic.co.jp"
                                    />
                                    {errors.tantoMail && (
                                        <div className="text-red-500 text-xs mt-1">
                                            {errors.tantoMail.message}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label className="font-medium text-gray-900">携帯電話</label>
                                <div className="md:col-span-2 flex gap-2">
                                    <div>
                                        <label htmlFor="mobile1" className="sr-only">携帯電話 1</label>
                                        <input
                                            id="mobile1"
                                            type="text"
                                            maxLength="5"
                                            {...register('mobile1')}
                                            onChange={(e) => {
                                                const value = validateNumeric(e.target.value)
                                                setValue('mobile1', value)
                                                trigger('mobile1')
                                            }}
                                            className="w-24 text-center px-2 py-2 text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="mobile2" className="sr-only">携帯電話 2</label>
                                        <input
                                            id="mobile2"
                                            type="text"
                                            maxLength="5"
                                            {...register('mobile2')}
                                            onChange={(e) => {
                                                const value = validateNumeric(e.target.value)
                                                setValue('mobile2', value)
                                                trigger('mobile2')
                                            }}
                                            className="w-24 text-center px-2 py-2 text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="mobile3" className="sr-only">携帯電話 3</label>
                                        <input
                                            id="mobile3"
                                            type="text"
                                            maxLength="5"
                                            {...register('mobile3')}
                                            onChange={(e) => {
                                                const value = validateNumeric(e.target.value)
                                                setValue('mobile3', value)
                                                trigger('mobile3')
                                            }}
                                            className="w-24 text-center px-2 py-2 text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 請求情報 Section */}
                        <div className="space-y-6">
                            <div className="bg-gray-800 text-white px-4 py-2 rounded">
                                請求情報
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                                <label className="font-medium text-gray-900">請求書送付先担当者名 *</label>
                                <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
                                    <div className="flex-1">
                                        <label htmlFor="seikyuTantoSei" className="sr-only">請求書送付先担当者姓</label>
                                        <input
                                            id="seikyuTantoSei"
                                            type="text"
                                            {...register('seikyuTantoSei')}
                                            onChange={(e) => {
                                                setValue('seikyuTantoSei', e.target.value)
                                                trigger('seikyuTantoSei')
                                            }}
                                            className={getBorderClass('seikyuTantoSei', 'w-full flex-1 px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            placeholder="(例)姓"
                                        />
                                        {errors.seikyuTantoSei && (
                                            <div className="text-red-500 text-xs mt-1">
                                                {errors.seikyuTantoSei.message}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <label htmlFor="seikyuTantoMei" className="sr-only">請求書送付先担当者名</label>
                                        <input
                                            id="seikyuTantoMei"
                                            type="text"
                                            {...register('seikyuTantoMei')}
                                            onChange={(e) => {
                                                setValue('seikyuTantoMei', e.target.value)
                                                trigger('seikyuTantoMei')
                                            }}
                                            className={getBorderClass('seikyuTantoMei', 'w-full flex-1 px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            placeholder="(例)名"
                                        />
                                        {errors.seikyuTantoMei && (
                                            <div className="text-red-500 text-xs mt-1">
                                                {errors.seikyuTantoMei.message}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label className="font-medium text-gray-900">請求書送付先担当者名（カナ） *</label>
                                <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
                                    <div className="flex-1">
                                        <label htmlFor="seikyuTantoSeiKana" className="sr-only">請求書送付先担当者姓（カナ）</label>
                                        <input
                                            id="seikyuTantoSeiKana"
                                            type="text"
                                            {...register('seikyuTantoSeiKana')}
                                            onChange={(e) => {
                                                setValue('seikyuTantoSeiKana', e.target.value)
                                                trigger('seikyuTantoSeiKana')
                                            }}
                                            onBlur={() => {
                                                trigger('seikyuTantoSeiKana')
                                            }}
                                            className={getBorderClass('seikyuTantoSeiKana', 'w-full flex-1 px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            placeholder="(例)セイ"
                                        />
                                        {errors.seikyuTantoSeiKana && (
                                            <div className="text-red-500 text-xs mt-1">
                                                {errors.seikyuTantoSeiKana.message}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <label htmlFor="seikyuTantoMeiKana" className="sr-only">請求書送付先担当者名（カナ）</label>
                                        <input
                                            id="seikyuTantoMeiKana"
                                            type="text"
                                            {...register('seikyuTantoMeiKana')}
                                            onChange={(e) => {
                                                setValue('seikyuTantoMeiKana', e.target.value)
                                                trigger('seikyuTantoMeiKana')
                                            }}
                                            onBlur={() => {
                                                trigger('seikyuTantoMeiKana')
                                            }}
                                            className={getBorderClass('seikyuTantoMeiKana', 'w-full flex-1 px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                            placeholder="(例)メイ"
                                        />
                                        {errors.seikyuTantoMeiKana && (
                                            <div className="text-red-500 text-xs mt-1">
                                                {errors.seikyuTantoMeiKana.message}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label htmlFor="seikyuTantoMail" className="font-medium text-gray-900">請求書送付先メールアドレス *</label>
                                <div className="md:col-span-2">
                                    <input
                                        id="seikyuTantoMail"
                                        type="email"
                                        {...register('seikyuTantoMail')}
                                        onChange={(e) => {
                                            setValue('seikyuTantoMail', e.target.value)
                                            trigger('seikyuTantoMail')
                                        }}
                                        className={getBorderClass('seikyuTantoMail', 'w-full px-3 py-2 text-gray-900 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50')}
                                        placeholder="(例)mail@inter-mic.co.jp"
                                    />
                                    {errors.seikyuTantoMail && (
                                        <div className="text-red-500 text-xs mt-1">
                                            {errors.seikyuTantoMail.message}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="text-center">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="bg-gray-800 text-white px-8 py-3 rounded-lg hover:bg-gray-700 disabled:opacity-50 text-lg font-medium tracking-wider"
                            >
                                {isLoading ? '更新中...' : '登録する'}
                            </button>
                        </div>

                        {/* ご利用プラン Section */}
                        <div className="space-y-6">
                            <div className="bg-gray-800 text-white px-4 py-2 rounded">
                                ご利用プラン
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label htmlFor="currentPlan" className="font-medium text-gray-900">ご利用プラン</label>
                                <div className="md:col-span-2">
                                    <input
                                        id="currentPlan"
                                        type="text"
                                        value={currentPlan}
                                        disabled
                                        className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <label htmlFor="changeRiyoPlan" className="font-medium text-gray-900">変更したいプラン</label>
                                <div className="md:col-span-2 flex gap-2 items-center">
                                    <select
                                        id="changeRiyoPlan"
                                        {...register('changeRiyoPlan')}
                                        onChange={(e) => {
                                            setValue('changeRiyoPlan', e.target.value)
                                            trigger('changeRiyoPlan')
                                        }}
                                        className="flex-1 px-3 py-2 text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                                    >
                                        <option value="">----------</option>
                                        <option value="0">デモプラン</option>
                                        <option value="1">スタートプラン</option>
                                        <option value="2">スタンダードプラン</option>
                                        <option value="3">プラチナプラン</option>
                                        <option value="4">エンタープライズプラン</option>
                                    </select>
                                    <button
                                        type="button"
                                        onClick={handlePlanChange}
                                        disabled={isPlanChangeLoading}
                                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
                                    >
                                        {isPlanChangeLoading ? '申請中...' : '変更申請'}
                                    </button>
                                </div>
                            </div>

                            {/* 追加機能の利用 */}
                            <div className="space-y-6">
                                <div className="bg-gray-800 text-white px-4 py-2 rounded">追加機能の利用</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={watch('pixClientUsage') === 1}
                                            onChange={(e) => {
                                                setValue('pixClientUsage', e.target.checked ? 1 : 0)
                                                trigger('pixClientUsage')
                                            }}
                                            className="rounded border-gray-300"
                                        />
                                        <span className="text-gray-900">PIX Client</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={watch('pixCameraUsage') === 1}
                                            onChange={(e) => {
                                                setValue('pixCameraUsage', e.target.checked ? 1 : 0)
                                                trigger('pixCameraUsage')
                                            }}
                                            className="rounded border-gray-300"
                                        />
                                        <span className="text-gray-900">PIX カメラ</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={watch('voiceRecognitionUsage') === 1}
                                            onChange={(e) => {
                                                setValue('voiceRecognitionUsage', e.target.checked ? 1 : 0)
                                                trigger('voiceRecognitionUsage')
                                            }}
                                            className="rounded border-gray-300"
                                        />
                                        <span className="text-gray-900">音声認識</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={watch('ocrUsage') === 1}
                                            onChange={(e) => {
                                                setValue('ocrUsage', e.target.checked ? 1 : 0)
                                                trigger('ocrUsage')
                                            }}
                                            className="rounded border-gray-300"
                                        />
                                        <span className="text-gray-900">OCR</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={watch('thetaCameraUsage') === 1}
                                            onChange={(e) => {
                                                setValue('thetaCameraUsage', e.target.checked ? 1 : 0)
                                                trigger('thetaCameraUsage')
                                            }}
                                            className="rounded border-gray-300"
                                        />
                                        <span className="text-gray-900">THETA カメラ</span>
                                    </label>
                                </div>
                            </div>

                            <div className="text-right">
                                <button
                                    type="button"
                                    onClick={handleCancellation}
                                    disabled={isCancellationLoading}
                                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm"
                                >
                                    {isCancellationLoading ? '処理中...' : '解約手続き'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Error Dialog */}
            <MessageDialog
                isOpen={showErrorDialog}
                onClose={closeErrorDialog}
                title="メッセージ"
                message={errorMessage}
                type="error"
            />

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                isOpen={showConfirmDialog}
                onConfirm={handleConfirmAction}
                onCancel={closeConfirmDialog}
                title="確認"
                message={confirmMessage}
                confirmText="OK"
                cancelText="キャンセル"
            />
        </Layout>
    )
}

export default KigyoJohoKanri
